/** archetype.default — RULES-ENGINE.md. Parameters come from funnels/<code>.json → rules. */
import type { AnswerMap } from '../answers.ts';
import type { ArchetypeRule } from '../types.ts';

export interface ArchetypeResult {
  id: string;
  scores: Record<string, number>;
  matched: boolean;
}

type Entry = Record<string, number> | { gte: number; pts: number };

const isGte = (e: Entry): e is { gte: number; pts: number } =>
  typeof (e as { gte?: unknown }).gte === 'number' && typeof (e as { pts?: unknown }).pts === 'number';

/** the set of match keys an answer can satisfy: its value, its list values, its yes indices */
function matchKeys(answer: unknown): string[] {
  if (!answer || typeof answer !== 'object') return [];
  const a = answer as { value?: unknown; values?: unknown[]; yes?: unknown[]; order?: unknown[] };
  const keys: string[] = [];
  if (typeof a.value === 'string') keys.push(a.value);
  for (const list of [a.values, a.yes, a.order]) {
    if (Array.isArray(list)) for (const v of list) keys.push(String(v));
  }
  return keys;
}

function numberOf(answer: unknown): number | undefined {
  if (!answer || typeof answer !== 'object') return undefined;
  const v = (answer as { value?: unknown }).value;
  return typeof v === 'number' ? v : undefined;
}

export function computeArchetype(rule: ArchetypeRule, answers: AnswerMap): ArchetypeResult {
  const scores: Record<string, number> = {};

  for (const [id, table] of Object.entries(rule.score ?? {})) {
    let total = 0;
    for (const [answerKey, entry] of Object.entries(table)) {
      const answer = answers[answerKey];
      if (answer === undefined) continue;
      if (isGte(entry)) {
        const n = numberOf(answer);
        if (n !== undefined && n >= entry.gte) total += entry.pts;
        continue;
      }
      const keys = matchKeys(answer);
      for (const [want, pts] of Object.entries(entry)) {
        if (keys.includes(want)) total += pts;
      }
    }
    scores[id] = total;
  }

  const best = Math.max(0, ...Object.values(scores));
  if (best === 0) return { id: rule.default, scores, matched: false };

  const top = Object.entries(scores).filter(([, v]) => v === best).map(([k]) => k);
  if (top.length === 1) return { id: top[0], scores, matched: true };

  const order = rule.tieBreak ?? [];
  const winner = order.find((id) => top.includes(id)) ?? top[0];
  return { id: winner, scores, matched: true };
}
